/**
 * Guards the status line (`#status`) against being clobbered by a
 * slow-resolving background write that started before something more
 * specific was shown (issue #84).
 *
 * `main.ts`'s boot sequence fires a background "fill to horizon" tile load
 * that isn't awaited by anything; its `.then()` used to call `setStatus`
 * unconditionally, so if it resolved after a later interaction (a viewpoint
 * jump, a mode switch, a BLOCKER) it would silently overwrite that newer
 * message with the stale boot-time tile count.
 *
 * The fix is a monotonic generation counter: every `setStatus` call commits
 * a new generation and returns its token. A deferred write only applies if
 * its captured token is still the most recent generation — i.e. nothing else
 * has written to the status line since. This needs no special-casing for
 * BLOCKER text specifically: a BLOCKER message is just another commit, so it
 * naturally wins over any older token exactly like any other newer status
 * would.
 */
export interface StatusSequencer {
  /** Record a status write and return its generation token. */
  commit(): number;
  /** True if `token` is still the most recent commit (nothing newer since). */
  isCurrent(token: number): boolean;
}

export function createStatusSequencer(): StatusSequencer {
  let generation = 0;
  return {
    commit(): number {
      generation += 1;
      return generation;
    },
    isCurrent(token: number): boolean {
      return token === generation;
    },
  };
}
